import { Database, ArrowUpRight, MinusCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { OPS } from "@/constants/testIds";
import { useDataGap } from "@/hooks/useDataStatus";

/**
 * A card with nothing to show NEVER says just "no data".
 *  - Month the data cannot exist for (before the POS era, or still in the future)
 *    -> "—" (not applicable).
 *  - Otherwise -> greyed panel + ONE line saying exactly why + a button that
 *    takes the user to the screen that closes the gap (only when they may open it).
 *
 * Pass `dataset` (a data-audit dataset key) to get the real reason. `month` is
 * inherited from the nearest <DataScope> when not given explicitly.
 */
export const EmptyState = ({
    icon: Icon = Database,
    title,
    hint,
    testId,
    compact,
    dataset,
    month,
    action,
}) => {
    const { t, lang } = useI18n();
    const gap = useDataGap(dataset, month);
    const tid = testId || OPS.sales.emptyState;

    const reasonLine = gap.known
        ? (lang === "ar" ? gap.reason.ar : gap.reason.en)
        : hint || t("no_data_hint");

    if (gap.na) {
        return (
            <div
                data-testid={tid}
                data-empty-kind="na"
                data-reason-code={gap.code}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] text-center ${
                    compact ? "py-6 px-4" : "py-10 px-6 min-h-[160px]"
                }`}
            >
                <span className="text-3xl font-extrabold leading-none text-[color:var(--ink-faint)]">—</span>
                <p className="max-w-sm text-xs text-[color:var(--ink-faint)]">{reasonLine}</p>
            </div>
        );
    }

    const btn = action || (gap.fix ? { to: gap.fix, label: t(`dg_btn_${gap.code}`) } : null);

    return (
        <div
            data-testid={tid}
            data-empty-kind="gap"
            data-reason-code={gap.code || "unknown"}
            className={`flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[color:var(--border-strong)] bg-[color:var(--bg-surface-2)] text-center ${
                compact ? "py-6 px-4" : "py-10 px-6 min-h-[160px]"
            }`}
            style={{ filter: "saturate(0.35)" }}
        >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--bg-surface-3)]">
                <Icon className="h-5 w-5 text-[color:var(--ink-faint)]" />
            </span>
            {gap.known ? (
                <p className="max-w-sm text-sm font-medium text-[color:var(--ink-muted)]">{reasonLine}</p>
            ) : (
                <>
                    <p className="text-sm font-medium text-[color:var(--ink-muted)]">{title || t("no_data")}</p>
                    <p className="max-w-sm text-xs text-[color:var(--ink-faint)]">{reasonLine}</p>
                </>
            )}
            {btn ? (
                <Link to={btn.to} data-testid={`${tid}-fix`} style={{ filter: "saturate(2.8)" }}>
                    <Button variant="outline" size="sm" className="mt-0.5 h-8 gap-1.5 text-xs">
                        {btn.label}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </Button>
                </Link>
            ) : null}
        </div>
    );
};

/** Inline "—" for a single figure inside a populated card. */
export const NotApplicable = ({ label, testId }) => (
    <span
        data-testid={testId}
        title={label}
        className="inline-flex items-center gap-1 text-[color:var(--ink-faint)]"
    >
        <MinusCircle className="h-3 w-3" />
        <span className="font-semibold">—</span>
    </span>
);
